import * as angular from "angular";
import { CheckManager } from "../shared/check-manager";
import { Effort } from "../models/effort.model";
import { Campaign } from "../models/campaign.model";
import { Filter } from "../models/filter.model";
import { Group } from "../models/group.model";
import { ToastUtils } from "../helper/toast.helper";
import { GroupService } from "../services/group.service";
import { CanvassStatusDataProvider } from "./canvass-status.dp";
import { CampaignDataProvider } from "./campaign.dp";
import { ExportDataProvider } from "./export.dp";
import { Organisation } from "../models/organisation.model";
import { Assignment } from "../models/assignment.model";
import { SortState } from "../components/shared/sortable-header/sortable-header.directive";

export class GroupDataProvider {
    public readonly defaultColumns = [
        {
            id: "gname",
            name: "Name",
            selected: true,
            type: null,
            disabled: true,
        },
        {
            id: "contacts",
            name: "# Contacts",
            selected: true,
            type: "number",
        },
        {
            id: "houses",
            name: "# Houses",
            selected: true,
            type: "number",
        },
        { id: "gstatus", name: "Status", selected: true, type: null },
        { id: "group_type", name: "List Type", selected: true, type: null },
        {
            id: "assigned",
            name: "Assignment",
            selected: true,
            type: null,
        },
    ];
    public groups: Group[] = [];
    public groupsAll: Group[] | null = null;
    public assignmentGroups: Group[] | null = null;
    public mapGroups: Group[] | null = null;
    public allGroupsPromise: Promise<Group[]>;
    public assignmentGroupsPromise: Promise<Group[]>;
    public filterGroupsPromise: any;
    public effort: Effort = null;
    public filters = new Filter();
    public searchPromise: any;
    public listView = "people";
    public loading = true;
    public error = false;
    public localStorage: any;
    public activeCampaign: Campaign;
    public isLeaderView: boolean;
    public disableRefreshButton = false;
    public columns: object[] = [];
    public filterSearchPromise: any;
    public count?: number;
    public scrollLoading?: boolean;
    public statsLoading?: boolean;
    public statsError?: boolean;
    public currentSort: SortState = { column: null, direction: "desc" };

    private $q: any;
    private $http: any;
    private __env: any;
    private $timeout: any;
    private toast: ToastUtils;
    private modelsUtils: any;

    public service: GroupService;
    public canvassStatusDataProvider: CanvassStatusDataProvider;
    public campaignDataProvider: CampaignDataProvider;
    public checkManager: CheckManager;
    public exportDataProvider: ExportDataProvider;

    constructor(
        $q: any,
        __env: any,
        $timeout: any,
        $http: any,
        __app_type: string,
        GroupService: GroupService,
        CanvassStatusDataProvider: CanvassStatusDataProvider,
        CampaignDataProvider: CampaignDataProvider,
        ExportDataProvider: ExportDataProvider,
        ToastUtils: ToastUtils,
        ModelsUtils: any
    ) {
        this.$q = $q;
        this.$http = $http;
        this.__env = __env;
        this.$timeout = $timeout;
        this.toast = ToastUtils;
        this.modelsUtils = ModelsUtils;

        this.service = GroupService;
        this.canvassStatusDataProvider = CanvassStatusDataProvider;
        this.campaignDataProvider = CampaignDataProvider;
        this.exportDataProvider = ExportDataProvider;
        this.checkManager = new CheckManager(this.groups);

        this.activeCampaign = this.campaignDataProvider.getActiveCampaign();
        this.isLeaderView = __app_type === "LEADER";
    }

    setColumns(): void {
        this.columns =
            this.localStorage && this.localStorage.groupColumns
                ? this.localStorage.groupColumns
                : this.defaultColumns;

        angular.forEach(this.defaultColumns, (defaultColumn: any) => {
            let found = false;
            angular.forEach(this.columns, (column: any) => {
                if (defaultColumn.id == column.id) {
                    found = true;
                }
            });
            if (!found) {
                this.columns.push(defaultColumn);
            }
        });
    }

    getColumns(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            this.campaignDataProvider
                .getOrganisations()
                .then((organisation: Organisation) => {
                    this.localStorage = organisation.getLocalStorage();
                    this.setColumns();
                    this.addStatusesToColumns();
                    deferred.resolve(this.columns);
                });
        } else {
            this.localStorage = this.activeCampaign.getLocalStorage();
            this.setColumns();
            this.addStatusesToColumns();
            deferred.resolve(this.columns);
        }

        return deferred.promise;
    }

    addStatusesToColumns(): void {
        this.canvassStatusDataProvider
            .walkCanvassStatuses()
            .then((statuses: any[]) => {
                angular.forEach(statuses, (status: any) => {
                    let found = false;
                    angular.forEach(this.columns, (column: any) => {
                        if (status.id == column.id) {
                            found = true;
                        }
                    });
                    if (!found)
                        this.columns.push({
                            id: status.id,
                            name: status.name,
                            selected: true,
                            color: status.color,
                            type: "status",
                        });
                });
            });
    }

    setFilters(setFilter: Filter): void {
        this.filters = setFilter;
        this.filters.removeSelectedUsers();
        this.resetItems();
    }

    segment(group: Group): Promise<any> {
        const deferred = this.$q.defer();
        this.service.segment(group).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    /**
     * returns all of the data
     */
    getAllGroups(planner: boolean = false): Promise<Group[]> {
        const deferred = this.$q.defer();
        this.service.all({ effort: this.effort }).then(
            (response: object) => {
                const json = response["data"];
                this.groupsAll = [];
                this.mapGroups = [];
                for (let i = 0; i < json.data.length; i++) {
                    let group = this.modelsUtils.group(json.data[i]);

                    if (group.points) {
                        this.mapGroups.push(group);
                    }

                    this.groupsAll.push(group);
                }
                deferred.resolve(this.groupsAll);
            },
            (error: object) => {
                this.groupsAll = [];
                deferred.reject(error);
            }
        );
        this.allGroupsPromise = deferred.promise;
        return this.allGroupsPromise;
    }

    /**
     * returns all of the data
     */
    getAllAssignmentGroups(): Promise<Group[]> {
        const deferred = this.$q.defer();
        let lastGroup =
            this.assignmentGroups && this.assignmentGroups.length > 0
                ? this.assignmentGroups[this.assignmentGroups.length - 1]
                : null;
        let after =
            lastGroup && lastGroup.assignments.length > 0
                ? lastGroup.assignments[lastGroup.assignments.length - 1].id
                : null;

        this.$http({
            url: "/v2/group/assignment",
            method: "GET",
            params: {
                effort: this.effort,
                after,
                gid: lastGroup ? lastGroup.gid : null,
            },
        }).then(
            (response: object) => {
                const json = response["data"];

                // Store the NEW groups separately
                const newGroups: Group[] = [];

                if (!this.assignmentGroups) {
                    this.assignmentGroups = [];
                }

                for (let i = 0; i < json.data.length; i++) {
                    let group = this.modelsUtils.group(json.data[i]);
                    this.assignmentGroups.push(group);
                    newGroups.push(group); // Track new groups
                }

                // Resolve with ONLY the new groups, not all groups
                deferred.resolve(newGroups);
            },
            (error: object) => {
                this.assignmentGroups = [];
                deferred.reject(error);
            }
        );

        this.assignmentGroupsPromise = deferred.promise;
        return this.assignmentGroupsPromise;
    }

    getMapGroups(): Group[] {
        return this.mapGroups;
    }

    getGroupsBy(filter: any): Promise<Group[]> {
        if (this.filterSearchPromise) {
            this.filterSearchPromise.resolve("User cancelled");
        }
        this.filterSearchPromise = this.$q.defer();

        this.$http({
            url: this.__env.apiUrl() + "/group/filtered",
            method: "GET",
            params: {
                filters: filter.nonEmptyValues(),
                effort: this.effort,
                view: this.listView,
            },
            timeout: this.filterSearchPromise.promise,
        }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    let groups = [];
                    for (let i = 0; i < json.data.length; i++) {
                        groups.push(this.modelsUtils.group(json.data[i]));
                    }

                    this.filterSearchPromise.resolve(groups);
                } else {
                    this.filterSearchPromise.reject(json);
                }
            },
            (error: any) => this.filterGroupsPromise.reject(error)
        );

        return this.filterSearchPromise.promise;
    }

    getFilteredGroups(
        lastGroup: Group,
        scroll: boolean,
        sort?: SortState
    ): Promise<any> {
        if (
            this.effort &&
            Object.keys(this.filters).length &&
            (!angular.isDefined(this.count) || this.groups.length < this.count)
        ) {
            if (sort !== undefined) {
                this.currentSort = sort;
            }

            this.error = false;

            if (scroll) this.scrollLoading = true;
            else this.loading = true;

            let returnPromise = this.$q.defer();
            if (this.searchPromise) {
                this.searchPromise.resolve("user cancelled");
            }
            this.searchPromise = this.$q.defer();
            const myPromise = this.searchPromise;

            // Only clear our loading flag if our request is still the
            // current one. If a newer getFilteredGroups has replaced
            // searchPromise, it owns the loading state and we must not
            // clobber it.
            const clearLoading = (): void => {
                if (this.searchPromise !== myPromise) {
                    return;
                }
                if (scroll) this.scrollLoading = false;
                else this.loading = false;
            };

            let last: any = null;
            if (lastGroup) {
                last = {
                    gid: lastGroup.gid,
                    relevance: lastGroup.relevance,
                };
                if (this.currentSort.column) {
                    last.sortValue = (lastGroup as any)[this.currentSort.column];
                }
            }

            const sortParam: string | undefined =
                this.currentSort.column
                    ? `${this.currentSort.column}:${this.currentSort.direction}`
                    : undefined;

            this.getAllGroups().then((): void => {
                this.$http({
                    url: this.__env.apiUrl() + "/group/filtered",
                    method: "GET",
                    params: {
                        last: last,
                        filters: this.filters.nonEmptyValues(),
                        effort: this.effort,
                        view: this.listView,
                        count: this.count,
                        sort: sortParam,
                    },
                    timeout: this.searchPromise.promise,
                }).then(
                    (response: object) => {
                        const json = response["data"];

                        if (!last) {
                            this.groups.splice(0, this.groups.length);
                        }

                        if (json.success) {
                            let gids = [];
                            for (let i = 0; i < json.data.length; i++) {
                                gids.push(json.data[i].gid);
                                let group = this.groupWithID(json.data[i].gid);

                                if (!group) {
                                    this.groupsAll.push(
                                        this.modelsUtils.group(json.data[i])
                                    );
                                    group = this.groupWithID(json.data[i].gid);
                                }

                                group.setData(json.data[i]);
                                this.checkManager.checkNewItem(group);
                                this.groups.push(group);
                            }
                            // Stats fires-and-forgets — it populates per-group
                            // chips but doesn't gate the list rendering. If a
                            // newer fetch aborts it, that fetch's stats call
                            // will repopulate. Suppress the unhandled rejection.
                            this.getFilteredGroupsStats(gids).catch(
                                (): void => {}
                            );

                            if (json.hasOwnProperty("count")) {
                                this.setFilteredGroupsCount(
                                    parseInt(json.count)
                                );
                            } else if (this.groups.length == 0) {
                                this.setFilteredGroupsCount(0);
                            }
                            returnPromise.resolve(this.groups);
                        } else {
                            returnPromise.reject(response);
                        }

                        clearLoading();
                    },
                    (error: any) => {
                        if (error.status > -1) {
                            this.error = true;
                        }
                        clearLoading();
                        returnPromise.reject(error);
                    }
                );
            });
            return returnPromise.promise;
        }
    }

    getFilteredGroupsStats(gids: string[]): Promise<Group[]> {
        this.statsLoading = true;
        if (!gids) {
            gids = this.groups.map((group) => group.gid);
        }

        const deferred = this.$q.defer();
        this.$http({
            url: this.__env.apiUrl() + "/group/filteredStats",
            method: "GET",
            params: {
                filters: this.filters.nonEmptyValues(),
                effort: this.effort,
                view: this.listView,
                gids: [gids],
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    angular.forEach(
                        json.data,
                        (statuses: object, gid: string) => {
                            angular.forEach(this.groups, (group: Group) => {
                                if (group.gid == gid) {
                                    group.statuses = statuses;
                                }
                            });
                        }
                    );

                    this.statsLoading = false;
                    deferred.resolve(this.groups);
                } else {
                    this.statsLoading = false;
                    deferred.reject(response);
                }
            },
            (error: any) => {
                this.statsLoading = false;
                if (error.status > -1) {
                    this.statsError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    getFilteredGroupsCount(): number {
        return this.count;
    }

    setFilteredGroupsCount(count: number): void {
        this.count = count;
        this.checkManager.setTotalCount(count);
    }

    setSort(sort: SortState): void {
        this.currentSort = sort;
    }

    resetMapGroups(): void {
        this.mapGroups.splice(0, this.mapGroups.length);
        for (let i = 0; i < this.groupsAll.length; i++) {
            if (this.groupsAll[i].points)
                this.mapGroups.push(this.groupsAll[i]);
        }
    }

    setEffort(newEffort: Effort): void {
        if (newEffort !== this.effort) {
            this.effort = newEffort;
            this.resetItems();
        }
    }

    resetItems(): void {
        this.setFilteredGroupsCount(undefined);
        this.getFilteredGroups(null, false);
    }

    clearAll(): void {
        this.searchPromise = null;
        this.groupsAll = null;
        this.assignmentGroups = null;
        this.mapGroups = null;
        this.filters = new Filter();
        this.allGroupsPromise = null;
        this.assignmentGroupsPromise = null;
        this.filterGroupsPromise = null;
        this.groups.splice(0, this.groups.length);
    }

    groupWithID(gid: string): Group {
        for (let i = 0; i < this.groupsAll.length; i++) {
            if (this.groupsAll[i].gid == gid) {
                return this.groupsAll[i];
            }
        }
        return null;
    }

    getGroup(gid: string): Promise<Group> {
        const deferred = this.$q.defer();
        this.service.info(gid).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(this.modelsUtils.group(json.data));
            }
            deferred.reject();
        });
        return deferred.promise;
    }

    getCount(data: object): Promise<any> {
        const deferred = this.$q.defer();
        this.service.getCount(data).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => {
                deferred.reject(response);
            }
        );
        return deferred.promise;
    }

    //This will include http request in future
    createGroup(groupData: Group): Promise<Group> {
        const deferred = this.$q.defer();
        this.service.create(groupData).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    let group = this.modelsUtils.group(json.data);
                    this.replaceGroup(group);
                    if (this.groups) {
                        this.groups.unshift(group);
                    }
                    deferred.resolve(group);
                } else {
                    deferred.reject(this.modelsUtils.group(json));
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    bulkRemove(gid: string, data: any): Promise<any> {
        const deferred = this.$q.defer();

        data.gid = gid;
        this.service.removeMembers(gid, data).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.resetItems();
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    spliceGroup(group: Group): void {
        //Remove team from list
        if (this.groupsAll !== null) {
            this.$timeout(() => {
                let index = this.groupsAll.indexOf(group);
                if (index > -1) {
                    this.groupsAll.splice(index, 1);
                    this.resetMapGroups();
                }
            });
        }
    }

    removeGroup(group: Group): Promise<any> {
        const deferred = this.$q.defer();
        this.service.remove(group.gid).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                this.spliceGroup(group);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }
    deleteAssignment(assignment: Assignment): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .removeAssignment(assignment.id)
            .then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            });
        return deferred.promise;
    }

    bulkDelete(): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .bulk_delete(this.checkManager.bulkActionData(this.filters))
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        this.clearAndResetCheckItems();
                        deferred.resolve(json.data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => deferred.reject(error)
            );
        return deferred.promise;
    }

    bulkUpdate(status: string): Promise<any> {
        const deferred = this.$q.defer();
        let bulkActionData = this.checkManager.bulkActionData(this.filters);
        bulkActionData["status"] = status;
        this.service.bulk_update(bulkActionData).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.clearAndResetCheckItems();
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    bulkAssign(assignment: Assignment) {
        const deferred = this.$q.defer();
        let bulkActionData = this.checkManager.bulkActionData(this.filters);
        bulkActionData["assignment"] = assignment;
        this.service.bulk_assignment(bulkActionData).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.clearAndResetCheckItems();
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    refreshDynamicList(group: Group): Promise<any> {
        this.disableRefreshButton = true;
        group.refreshingContacts = true;
        const deferred = this.$q.defer();
        this.service.refreshList(group).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => {
                this.disableRefreshButton = false;
                group.refreshingContacts = false;
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    bulkRefreshDynamicList(): Promise<any> {
        this.disableRefreshButton = true;
        this.setContactsRefreshing();
        const deferred = this.$q.defer();
        this.service
            .bulkRefreshList(this.checkManager.bulkActionData(this.filters))
            .then(
                (response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        deferred.resolve(json.data);
                        this.checkManager.checkAll(false);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: object) => {
                    this.disableRefreshButton = false;
                    deferred.reject(error);
                }
            );
        return deferred.promise;
    }

    setContactsRefreshing(): void {
        let checkedListsArray = this.groups.filter(
            (group) =>
                this.checkManager.checked_items.indexOf(group.gid) > -1 &&
                group.fid
        );

        angular.forEach(checkedListsArray, (list) => {
            list.refreshingContacts = !list.refreshingContacts;
        });
    }

    clearAndResetCheckItems(): void {
        this.allGroupsPromise = null;
        this.groupsAll = null;
        this.resetItems();
        this.checkManager.removeCheckedItems();
    }

    replaceGroup(group: Group): void {
        this.$timeout(() => {
            let existingGroup = this.groupWithID(group.gid);
            if (existingGroup) {
                existingGroup.setData(group);
            } else {
                this.groupsAll.unshift(group);
            }
            this.resetMapGroups();
        });
    }

    bulkExport(exportChoices: any): Promise<any> {
        const deferred = this.$q.defer();
        let exportData = this.checkManager.bulkActionData(this.filters);
        exportData["data_type"] = "groups";
        exportData["exportChoices"] = exportChoices;
        exportData["timezone"] =
            Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.exportDataProvider.create(exportData).then(
            (data: object) => deferred.resolve(data),
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    processSocketUpdate(socketData: any): void {
        if (socketData.dynamicList) {
            this.disableRefreshButton = false;
            if (socketData.gname) {
                this.toast.success(
                    `Your list ${socketData.gname} is now up to date`
                );
            } else {
                this.toast.success(
                    `Your list ${
                        (Object.values(socketData)[0] as any).gname
                    } and ${socketData.count} more lists are now up to date`
                );
            }
            this.clearAndResetCheckItems();
        }
        if (this.groupsAll) {
            this.replaceGroup(this.modelsUtils.group(socketData));
        }
    }

    processSocketCreate(socketData: object): void {
        if (this.groupsAll) {
            this.replaceGroup(this.modelsUtils.group(socketData));
        }
    }

    processSocketDelete(socketData: any): void {
        if (this.groupsAll) {
            let existingGroup = this.groupWithID(socketData.gid);
            if (existingGroup) {
                this.spliceGroup(existingGroup);
            }
        }
    }
}

angular
    .module("vcDataProvider")
    .service("GroupDataProvider", GroupDataProvider);
